import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

export default function Banking() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [unassigned,setUnassigned]=useState([]);
  const [reviewError,setReviewError]=useState('');
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('accounts');
  const [showAdd, setShowAdd] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustAccount, setAdjustAccount] = useState(null);
  const [editAccount, setEditAccount] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const isSuperAdmin = user?.role === 'super_admin';

  if (!isSuperAdmin) {
    return (
      <div className="empty-state">
        <h3>Access Restricted</h3>
        <p>Banking information is only available to the Super Admin.</p>
      </div>
    );
  }

  const loadAccounts = () => {
    get('/bank-accounts').then(d => setAccounts(d.data||[])).finally(() => setLoading(false));
    get('/bank-accounts/unassigned-summary').then(d=>{setUnassigned(d.data||[]);setReviewError('');}).catch(e=>setReviewError(e.message));
  };

  const loadTransfers = () => {
    get('/bank-accounts/transfers').then(d => setTransfers(d.data||[]));
  };

  useEffect(() => { loadAccounts(); loadTransfers(); }, []);

  const loadTransactions = (id) => {
    get('/bank-accounts/'+id+'/transactions').then(d => setTransactions(d.data||[]));
  };

  const totalBalance = accounts.reduce((sum, a) => sum + parseFloat(a.calculated_balance||0), 0);

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Banking</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Manage bank accounts and transfers</p>
        </div>
        <div className="flex-center gap-sm">
          <button className="btn btn-outline btn-sm" onClick={()=>{loadAccounts();loadTransfers();}}>Refresh Balances</button>
          <button className="btn btn-outline btn-sm" onClick={() => setShowTransfer(true)}>Transfer Funds</button>
          {isSuperAdmin && <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Account</button>}
        </div>
      </div>

      {reviewError && <div className="alert alert-error">Unable to load reconciliation status: {reviewError}</div>}
      {unassigned.some(r=>r.count>0) && <div className="alert alert-warning" style={{marginBottom:16}}>
        <strong>{unassigned.reduce((n,r)=>n+Number(r.count),0)} entries await account assignment.</strong>
        <p>Balances below include assigned entries only. Reconcile the unassigned entries against your statements before relying on these balances.</p>
        {unassigned.map(r=><div key={r.account}>{r.account}: {r.count} entries; net {fmt(r.net_amount)}</div>)}
      </div>}
      {/* Total balance tile */}
      <div style={{background:'#556B2F',borderRadius:10,padding:'18px 24px',marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:'.12em',textTransform:'uppercase',color:'rgba(242,239,231,.5)'}}>Total Funds Across All Accounts</div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'2rem',fontWeight:700,color:'#A8893D',marginTop:4}}>{fmt(totalBalance)}</div>
        </div>
        <div style={{fontSize:11,color:'rgba(242,239,231,.4)'}}>{accounts.length} account{accounts.length!==1?'s':''}</div>
      </div>

      {/* View tabs */}
      <div className="flex-center gap-sm" style={{marginBottom:16}}>
        <button className={'btn btn-sm '+(view==='accounts'?'btn-primary':'btn-outline')} onClick={() => { setView('accounts'); setSelectedAccount(null); }}>Accounts</button>
        <button className={'btn btn-sm '+(view==='transfers'?'btn-primary':'btn-outline')} onClick={() => setView('transfers')}>Transfers</button>
      </div>

      {loading ? <div className="spinner"></div> : view === 'accounts' ? (
        <div>
          {/* Account cards */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:14,marginBottom:20}}>
            {accounts.map(a => (
              <div key={a.id}
                style={{background:'#fff',border:'2px solid '+(selectedAccount?.id===a.id?'#556B2F':'var(--border-light)'),
                  borderRadius:10,padding:'16px 18px',cursor:'pointer',boxShadow:'var(--shadow)',transition:'all .15s'}}
                onClick={() => {
                  setSelectedAccount(a);
                  loadTransactions(a.id);
                }}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:14,color:'#3B2A21'}}>{a.name}</div>
                    <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>{a.bank_name}</div>
                    {a.account_number && <div style={{fontSize:12,color:'#555',marginTop:2,letterSpacing:'.04em'}}>{a.account_number}</div>}
                  </div>
                  <div style={{display:'flex',gap:6,alignItems:'center'}}>
                    {a.is_default && <span className="badge b-paid" style={{fontSize:9}}>Default</span>}
                    <span className={'badge '+(a.type==='cash'?'b-grey':'b-blue')} style={{fontSize:9}}>{a.type}</span>
                  </div>
                </div>
                <div style={{borderTop:'1px solid var(--border-light)',paddingTop:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:10,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em'}}>Balance</span>
                  <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:700,
                    color:parseFloat(a.calculated_balance)>=0?'#556B2F':'#8F2E24'}}>
                    {fmt(a.calculated_balance)}
                  </span>
                </div>
                {isSuperAdmin && (
                  <div style={{display:'flex',gap:6,marginTop:10}}>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11,flex:1}}
                      onClick={e => { e.stopPropagation(); setEditAccount(a); }}>
                      Edit Details
                    </button>
                    <button className="btn btn-outline btn-sm" style={{fontSize:11,flex:1,color:'#556B2F',borderColor:'#556B2F'}}
                      onClick={e => { e.stopPropagation(); setAdjustAccount(a); setShowAdjust(true); }}>
                      Adjust Balance
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Transactions for selected account */}
          {selectedAccount && (
            <div className="card">
              <div className="card-hdr">
                <span className="card-title">Transactions — {selectedAccount.name}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => setSelectedAccount(null)}>Close</button>
              </div>
              {transactions.length === 0
                ? <div className="empty-state" style={{padding:24}}><p>No transactions yet</p></div>
                : <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                    <thead>
                      <tr style={{background:'var(--cream)'}}>
                        <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Date</th>
                        <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Description</th>
                        <th style={{padding:'9px 16px',textAlign:'left',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Type</th>
                        <th style={{padding:'9px 16px',textAlign:'right',fontSize:10,fontWeight:600,letterSpacing:'.08em',textTransform:'uppercase',color:'var(--ink-light)'}}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(t => (
                        <tr key={t.id}>
                          <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)'}}>{t.date}</td>
                          <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)'}}>{t.description}</td>
                          <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)'}}>
                            <span className={'badge '+(t.type==='inflow'?'b-paid':'b-unpaid')}>{t.type}</span>
                          </td>
                          <td style={{padding:'10px 16px',borderBottom:'1px solid var(--border-light)',textAlign:'right',
                            fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:600,
                            color:t.type==='inflow'?'var(--paid)':'var(--unpaid)'}}>
                            {t.type==='inflow'?'+':'-'}{fmt(t.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
          )}
        </div>
      ) : (
        /* Transfers view */
        <div className="tbl-wrap">
          {transfers.length === 0
            ? <div className="empty-state"><h3>No transfers yet</h3></div>
            : <table>
                <thead>
                  <tr>
                    <th>Date</th><th>From</th><th>To</th><th>Reference</th>
                    <th style={{textAlign:'right'}}>Amount</th><th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map(t => (
                    <tr key={t.id}>
                      <td>{t.date}</td>
                      <td><span style={{fontWeight:500}}>{t.from_account_name}</span><br/><span className="text-muted text-xs">{t.from_bank}</span></td>
                      <td><span style={{fontWeight:500}}>{t.to_account_name}</span><br/><span className="text-muted text-xs">{t.to_bank}</span></td>
                      <td className="text-muted text-sm">{t.reference||'—'}</td>
                      <td className="amt" style={{color:'var(--forest)'}}>{fmt(t.amount)}</td>
                      <td className="text-muted text-sm">{t.created_by_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      )}

      {showAdd && <AccountModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadAccounts(); }} />}
      {editAccount && <AccountModal account={editAccount} onClose={() => setEditAccount(null)} onSaved={() => { setEditAccount(null); loadAccounts(); }} />}
      {showAdjust && adjustAccount && (
        <AdjustBalanceModal
          account={adjustAccount}
          onClose={()=>{setShowAdjust(false);setAdjustAccount(null);}}
          onSaved={()=>{setShowAdjust(false);setAdjustAccount(null);loadAccounts();}}
        />
      )}
      {showTransfer && <TransferModal accounts={accounts} onClose={() => setShowTransfer(false)} onSaved={() => { setShowTransfer(false); loadAccounts(); loadTransfers(); }} />}
    </div>
  );
}

function AccountModal({ account, onClose, onSaved }) {
  const isEdit = !!account?.id;
  const [form, setForm] = useState(account || {
    name:'', bank_name:'', account_number:'', account_name:'Bloans Creations Ltd',
    type:'bank', is_default:false, opening_balance:''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.name || !form.bank_name) { setError('Name and bank required'); return; }
    setSaving(true);
    try {
      if (isEdit) await put('/bank-accounts/'+account.id, form);
      else await post('/bank-accounts', form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:480}}>
        <div className="modal-hdr">
          <span className="modal-title">{isEdit?'Edit':'New'} Bank Account</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">Account Nickname *</label>
            <input className="form-control" placeholder="e.g. Moniepoint Main, UBA Savings"
              value={form.name} onChange={e => setForm({...form, name:e.target.value})} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Bank Name *</label>
              <input className="form-control" placeholder="e.g. Moniepoint MFB"
                value={form.bank_name} onChange={e => setForm({...form, bank_name:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Account Type</label>
              <select className="form-control" value={form.type} onChange={e => setForm({...form, type:e.target.value})}>
                <option value="bank">Bank</option>
                <option value="cash">Cash</option>
                <option value="mobile_money">Mobile Money</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Account Number</label>
              <input className="form-control" value={form.account_number||''}
                onChange={e => setForm({...form, account_number:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Account Name</label>
              <input className="form-control" value={form.account_name||''}
                onChange={e => setForm({...form, account_name:e.target.value})} />
            </div>
          </div>
          {!isEdit && (
            <div className="form-group">
              <label className="form-label">Opening Balance (N)</label>
              <input type="number" className="form-control" placeholder="Current balance in this account"
                value={form.opening_balance||''} onChange={e => setForm({...form, opening_balance:e.target.value})} />
              <div className="form-hint">Enter the current balance to start tracking from today</div>
            </div>
          )}
          <div style={{display:'flex',alignItems:'center',gap:10,marginTop:8}}>
            <input type="checkbox" id="is_default" checked={form.is_default||false}
              onChange={e => setForm({...form, is_default:e.target.checked})} />
            <label htmlFor="is_default" style={{fontSize:13,color:'var(--ink-mid)'}}>Set as default account for payments</label>
          </div>
          {isEdit && (
            <div style={{display:'flex',alignItems:'center',gap:10,marginTop:8}}>
              <input type="checkbox" id="is_active" checked={form.is_active!==false}
                onChange={e => setForm({...form, is_active:e.target.checked})} />
              <label htmlFor="is_active" style={{fontSize:13,color:'var(--ink-mid)'}}>Account is active</label>
            </div>
          )}
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Saving...':isEdit?'Save Changes':'Add Account'}
          </button>
        </div>
      </div>
    </div>
  );
}


function AdjustBalanceModal({ account, onClose, onSaved }) {
  const [form, setForm] = useState({
    actual_balance: '',
    reason: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);
  const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');
  const current = parseFloat(account.calculated_balance||0);

  const submit = async () => {
    if (!form.actual_balance) { alert('Please enter the actual balance'); return; }
    if (!form.reason) { alert('Please provide a reason for this adjustment'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('bb_token');
      const res = await fetch('/api/bank-accounts/'+account.id+'/adjust', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:'Bearer '+token },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onSaved();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const actual = parseFloat(form.actual_balance||0);
  const diff = actual - current;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:440}}>
        <div className="modal-hdr">
          <span className="modal-title">Adjust Balance — {account.name}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          <div style={{background:'#F2EFE7',borderRadius:8,padding:12,marginBottom:16}}>
            <div style={{fontSize:11,color:'#8A8A8A',marginBottom:4}}>Current Recorded Balance</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',
              fontWeight:700,color:current>=0?'#556B2F':'#8F2E24'}}>
              {fmt(current)}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Actual Balance (₦) *</label>
            <input type="number" className="form-control" value={form.actual_balance}
              onChange={e=>setForm({...form,actual_balance:e.target.value})}
              placeholder="Enter the actual bank balance" step="0.01" />
            <div className="form-hint">Check your bank statement or app for the correct balance</div>
          </div>
          {form.actual_balance && (
            <div style={{background: diff>=0?'#E8F5EB':'#FDEAEA',
              border:'1px solid '+(diff>=0?'#B8DEC0':'#F5B8B8'),
              borderRadius:8,padding:10,marginBottom:12,fontSize:12}}>
              <strong>Adjustment: </strong>
              <span style={{color:diff>=0?'#2E6B3A':'#8F2E24',fontWeight:600}}>
                {diff>=0?'+':''}{fmt(diff)}
              </span>
              {' '}({diff>=0?'Credit':'Debit'} adjustment)
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Date</label>
            <input type="date" className="form-control" value={form.date}
              onChange={e=>setForm({...form,date:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Reason *</label>
            <textarea className="form-control" rows={2} value={form.reason}
              onChange={e=>setForm({...form,reason:e.target.value})}
              placeholder="e.g. Opening balance correction, Bank charges not recorded, etc." />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving||!form.actual_balance||!form.reason}>
            {saving?'Saving...':'Apply Adjustment'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TransferModal({ accounts, onClose, onSaved }) {
  const [form, setForm] = useState({
    from_account_id:'', to_account_id:'',
    date: new Date().toISOString().split('T')[0],
    amount:'', reference:'', notes:''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fromAcc = accounts.find(a => a.id === form.from_account_id);

  const submit = async () => {
    if (!form.from_account_id || !form.to_account_id || !form.amount || !form.date) {
      setError('All fields required'); return;
    }
    if (form.from_account_id === form.to_account_id) {
      setError('Cannot transfer to the same account'); return;
    }
    setSaving(true);
    try {
      await post('/bank-accounts/transfer', form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:460}}>
        <div className="modal-hdr">
          <span className="modal-title">Transfer Funds</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">From Account *</label>
            <select className="form-control" value={form.from_account_id} onChange={e => setForm({...form, from_account_id:e.target.value})}>
              <option value="">Select account</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name} — {fmt(a.calculated_balance)}</option>
              ))}
              <option value="petty_cash">Petty Cash</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">To Account *</label>
            <select className="form-control" value={form.to_account_id} onChange={e => setForm({...form, to_account_id:e.target.value})}>
              <option value="">Select account</option>
              {accounts.filter(a => a.id !== form.from_account_id).map(a => (
                <option key={a.id} value={a.id}>{a.name} — {fmt(a.calculated_balance)}</option>
              ))}
              {form.from_account_id !== 'petty_cash' && <option value="petty_cash">Petty Cash</option>}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" className="form-control" value={form.date} onChange={e => setForm({...form, date:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Amount (N) *</label>
              <input type="number" className="form-control" value={form.amount} onChange={e => setForm({...form, amount:e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Reference</label>
            <input className="form-control" placeholder="Transfer reference..." value={form.reference} onChange={e => setForm({...form, reference:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-control" value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Transferring...' : 'Transfer Funds'}
          </button>
        </div>
      </div>
    </div>
  );
}
